import React from 'react';
import moment from 'moment';
import {
  SBody,
  SButton,
  SCaption,
  SCard,
  SGrid,
  SIcon,
  SLink,
  SModal,
} from '@chargebee/sting-react';
import {
  useDaysToExpiration,
  type Expiration,
} from '@bbkAdminRedux/rtkq/checkout.slice';
import { useSelectCurrentApp } from '@bbkAdminRedux/rtkq/current-user.slice';
import { useGetProductLicense } from '@bbkAdminUtils/plans';
import { useRouteMatch } from '@bbkShared/react-router';

type TrialExpiryEligibility = {
  expiration: Expiration | undefined;
  hasGrowthV2License: boolean;
  inAdminApp: boolean;
  now?: moment.Moment;
};

export const shouldShowTrialExpiryExperience = ({
  expiration,
  hasGrowthV2License,
  inAdminApp,
  now = moment.utc(),
}: TrialExpiryEligibility): boolean =>
  Boolean(
    inAdminApp &&
    hasGrowthV2License &&
    expiration?.isTrial &&
    expiration.endDate.isBefore(now)
  );

export const useShowTrialExpiryExperience = (): boolean => {
  const expiration = useDaysToExpiration();
  const productLicense = useGetProductLicense();
  const inAdminApp = useRouteMatch('/company/:companyKey/app/:appKey');

  return shouldShowTrialExpiryExperience({
    expiration,
    hasGrowthV2License: Boolean(productLicense?.growthV2License),
    inAdminApp: Boolean(inAdminApp),
  });
};

export const useSuppressExpiredTrialBanner = (): boolean => {
  const currentApp = useSelectCurrentApp();
  return (
    useShowTrialExpiryExperience() && Boolean(currentApp?.show_play_demo_data)
  );
};

type TrialExpiryModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const pausedCapabilities = [
  {
    icon: 'message-square-x' as const,
    title: 'Cancel flows stop',
    description: 'Exit surveys error out — customers cancel undeflected.',
  },
  {
    icon: 'gift' as const,
    title: 'Offers go dark',
    description: 'Lifecycle offers, targeting and routing stop serving.',
  },
  {
    icon: 'chart-column' as const,
    title: 'Reporting pauses',
    description: 'Metrics exports and dashboards stop refreshing.',
  },
  {
    icon: 'blocks' as const,
    title: 'Integrations idle',
    description: 'Billing, Salesforce and domain syncs are skipped.',
  },
];

export const TrialExpiryNavCard: React.FC<{ onClick: () => void }> = ({
  onClick,
}) => (
  <SButton
    type="button"
    variant="neutral-ghost"
    fullWidth
    onClick={onClick}
    className="tw-group tw-h-auto tw-justify-start tw-whitespace-normal tw-px-3 tw-py-0 tw-text-left"
    data-testid="trial-expiry-nav-card"
  >
    <SCard
      padding="none"
      spacey="none"
      background="warning"
      className="tw-flex tw-w-full tw-items-center tw-gap-2.5 !tw-border-warn-200 tw-p-2.5 tw-transition-colors hover:!tw-bg-warn-50"
    >
      <span className="tw-flex tw-size-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-gradient-to-br tw-from-warn-400 tw-to-warn-600 tw-text-white">
        <SIcon name="triangle-alert" size={14} />
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <SBody className="tw-font-semibold tw-text-warn-900">
          Trial period expired
        </SBody>
        <SCaption as="p" className="tw-text-warn-700">
          See what you&apos;re missing
        </SCaption>
      </span>
      <SIcon
        name="arrow-right"
        size={14}
        className="tw-shrink-0 tw-text-warn-700 tw-transition-transform group-hover:tw-translate-x-0.5"
      />
    </SCard>
  </SButton>
);

export const TrialExpiryModal: React.FC<TrialExpiryModalProps> = ({
  open,
  onOpenChange,
}) => (
  <SModal open={open} onOpenChange={onOpenChange}>
    <SModal.Content
      size="regular"
      padding="xsmall"
      space="xsmall"
      className="tw-overflow-hidden md:tw-max-w-[576px]"
      data-testid="trial-expiry-modal"
    >
      <SModal.Header
        showCloseButton
        className="-tw-m-[2px] tw-mb-0 tw-bg-gradient-to-br tw-from-primary-600 tw-to-primary-800 tw-px-6 tw-py-5 !tw-text-white"
      >
        <SModal.Title className="tw-flex tw-items-center tw-gap-2.5 !tw-text-white">
          <span className="tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/25">
            <SIcon name="triangle-alert" size={16} />
          </span>
          Trial period expired
        </SModal.Title>
        <SModal.Description className="!tw-text-white/85">
          Your account is inactive. These capabilities are paused until full
          access is restored.
        </SModal.Description>
      </SModal.Header>
      <SModal.Body>
        <SGrid
          gap="regular"
          columnTemplate="repeat(auto-fit, minmax(200px, 1fr))"
          className="tw-px-6 tw-py-5"
        >
          {pausedCapabilities.map((capability) => (
            <SCard
              key={capability.title}
              padding="none"
              spacey="small"
              className="tw-p-3"
            >
              <SBody className="tw-flex tw-items-center tw-gap-2 tw-font-semibold tw-text-neutral-900">
                <SIcon
                  name={capability.icon}
                  size={16}
                  className="tw-shrink-0 tw-text-primary-600"
                />
                {capability.title}
              </SBody>
              <SCaption as="p" className="tw-text-neutral-500">
                {capability.description}
              </SCaption>
            </SCard>
          ))}
        </SGrid>
      </SModal.Body>
      <SModal.FooterNote className="tw-border-t tw-border-neutral-100 !tw-px-6">
        <SCaption as="p" className="tw-text-neutral-500">
          Your data is safe — nothing is deleted. Reactivation restores access.
        </SCaption>
        <SButton asChild className="tw-mt-3 tw-w-full">
          <SLink href="mailto:growth-support@chargebee.com?subject=Reactivate%20full%20features%20-%20trial%20expired">
            <SIcon name="mail" size={16} />
            Contact growth-support@chargebee.com
          </SLink>
        </SButton>
      </SModal.FooterNote>
    </SModal.Content>
  </SModal>
);
